import {
    xhrGetAsync,
    xhrPostAsync,
} from '~/libs/core'

import {
    TC_FINANCE_API_URL,
} from '../constants'
import type {
    AssignmentPayment,
} from '../models'

import {
    fetchBillingAccountById,
} from './billing-accounts.service'
import {
    searchProfilesByUserIds,
} from './users.service'

const DEFAULT_ENGAGEMENT_PAYMENT_STATUS = 'ON_HOLD_ADMIN'

interface PaymentDetailsPayload {
    billingAccount: string
    currency: string
    grossAmount: number
    installmentNumber: number
    totalAmount: number
}

interface MemberPaymentPayload {
    attributes: {
        agreementRate?: number | string
        assignmentId: number | string
        memberHandle: string
        remarks: string
        /**
         * Approved timesheet entries this payment covers. Traceability runs both ways: from the payment
         * to the entries here, and from each entry back to the payment via its stored reference.
         */
        timesheetEntryIds?: string[]
    }
    category: string
    description: string
    details: PaymentDetailsPayload[]
    externalId: string
    hoursWorked?: number
    origin: string
    status: string
    title: string
    type: string
    winnerId: string
}

interface PaymentsByAssignmentResponse {
    data?: AssignmentPayment[]
    result?: AssignmentPayment[]
}

export interface TimesheetPaymentConflict {
    entryIds: string[]
    paymentReference: string
}

interface TimesheetPaymentConflictSummary {
    conflicts: TimesheetPaymentConflict[]
    overlappingEntryIds: string[]
}

function normalizeError(error: unknown, fallbackMessage: string): Error {
    const typedError = error as {
        message?: string
        response?: {
            data?: {
                message?: string
            }
        }
    }

    const message = typedError?.response?.data?.message
        || typedError?.message
        || fallbackMessage

    return new Error(message)
}

function normalizePaymentsResponse(response: unknown): AssignmentPayment[] {
    if (Array.isArray(response)) {
        return response
    }

    if (typeof response === 'object' && response) {
        const typedResponse = response as PaymentsByAssignmentResponse

        if (Array.isArray(typedResponse.data)) {
            return typedResponse.data
        }

        if (Array.isArray(typedResponse.result)) {
            return typedResponse.result
        }

        const nestedData = (typedResponse as {
            data?: {
                data?: AssignmentPayment[]
            }
        }).data?.data

        if (Array.isArray(nestedData)) {
            return nestedData
        }
    }

    return []
}

/**
 * Resolves creator handles for payment history rows when finance returns only
 * the creator user id.
 *
 * @param payments payment rows returned by the finance API.
 * @returns the original rows with `createdByHandle` hydrated when member data is available.
 *
 * @remarks Payment history remains usable even if the member lookup fails; in
 * that case this helper returns the original rows unchanged.
 *
 * @throws This helper does not raise exceptions.
 */
async function hydratePaymentCreatorHandles(
    payments: AssignmentPayment[],
): Promise<AssignmentPayment[]> {
    const creatorIds = Array.from(new Set(
        payments
            .map(payment => String(payment.createdBy || '')
                .trim())
            .filter(Boolean),
    ))

    if (!creatorIds.length) {
        return payments
    }

    try {
        const profiles = await searchProfilesByUserIds(creatorIds)
        const creatorHandlesByUserId = new Map(
            profiles
                .filter(profile => profile.handle)
                .map(profile => [profile.userId, profile.handle as string]),
        )

        return payments.map(payment => {
            const creatorId = String(payment.createdBy || '')
                .trim()
            const createdByHandle = creatorHandlesByUserId.get(creatorId)

            return createdByHandle
                ? {
                    ...payment,
                    createdByHandle,
                }
                : payment
        })
    } catch {
        return payments
    }
}

/**
 * Hydrates payment details with billing account names for payment history rows.
 *
 * @param payments payment rows returned by the finance API.
 * @returns the original rows with `details[].billingAccountName` added when a
 * billing account lookup succeeds.
 *
 * @remarks Lookup failures are ignored so payment history can still display
 * the stored BA ID returned by finance.
 *
 * @throws This helper does not raise exceptions.
 */
async function hydratePaymentBillingAccountNames(
    payments: AssignmentPayment[],
): Promise<AssignmentPayment[]> {
    const billingAccountIds = Array.from(new Set(
        payments
            .flatMap(payment => payment.details || [])
            .map(detail => String(detail.billingAccount || '')
                .trim())
            .filter(Boolean),
    ))

    if (!billingAccountIds.length) {
        return payments
    }

    const billingAccountNamesById = new Map<string, string>()

    await Promise.all(billingAccountIds.map(async billingAccountId => {
        try {
            const billingAccount = await fetchBillingAccountById(billingAccountId)
            const billingAccountName = String(billingAccount.name || '')
                .trim()

            if (billingAccountName) {
                billingAccountNamesById.set(billingAccountId, billingAccountName)
            }
        } catch {
            // Keep payment history usable even when a billing-account lookup fails.
        }
    }))

    if (!billingAccountNamesById.size) {
        return payments
    }

    return payments.map(payment => {
        if (!payment.details?.length) {
            return payment
        }

        let changed = false
        const details = payment.details.map(detail => {
            if (detail.billingAccountName) {
                return detail
            }

            const billingAccountId = String(detail.billingAccount || '')
                .trim()
            const billingAccountName = billingAccountNamesById.get(billingAccountId)

            if (!billingAccountName) {
                return detail
            }

            changed = true

            return {
                ...detail,
                billingAccountName,
            }
        })

        return changed
            ? {
                ...payment,
                details,
            }
            : payment
    })
}

export async function createMemberPayment(
    assignmentId: number | string,
    memberId: number | string,
    memberHandle: string,
    title: string,
    remarks: string,
    rate: number | string,
    amount: number | string,
    hoursWorked: number | string,
    billingAccountId: number | string,
    timesheetEntryIds: string[] = [],
): Promise<AssignmentPayment> {
    const numericAmount = Number(amount)
    const numericHoursWorked = Number(hoursWorked)

    const payload: MemberPaymentPayload = {
        attributes: {
            agreementRate: rate,
            assignmentId,
            memberHandle,
            remarks: remarks.trim(),
            timesheetEntryIds: timesheetEntryIds.length ? timesheetEntryIds : undefined,
        },
        category: 'ENGAGEMENT_PAYMENT',
        description: title.trim(),
        details: [
            {
                billingAccount: String(billingAccountId),
                currency: 'USD',
                grossAmount: numericAmount,
                installmentNumber: 1,
                totalAmount: numericAmount,
            },
        ],
        // Stays the assignment id: payment history is fetched by external id, and the entries a
        // payment consumed are carried in attributes instead.
        externalId: String(assignmentId),
        hoursWorked: Number.isFinite(numericHoursWorked) && numericHoursWorked > 0
            ? numericHoursWorked
            : undefined,
        origin: 'Topcoder',
        status: DEFAULT_ENGAGEMENT_PAYMENT_STATUS,
        title: title.trim(),
        type: 'PAYMENT',
        winnerId: String(memberId),
    }

    try {
        return xhrPostAsync<MemberPaymentPayload, AssignmentPayment>(
            `${TC_FINANCE_API_URL}/winnings`,
            payload,
        )
    } catch (error) {
        throw normalizeError(error, 'Failed to create payment')
    }
}

export async function fetchAssignmentPayments(
    assignmentId: number | string,
): Promise<AssignmentPayment[]> {
    try {
        const response = await xhrGetAsync<unknown>(
            `${TC_FINANCE_API_URL}/winnings/by-external-id/${assignmentId}`,
        )

        const payments = await hydratePaymentCreatorHandles(normalizePaymentsResponse(response))

        return hydratePaymentBillingAccountNames(payments)
    } catch (error) {
        throw normalizeError(error, 'Failed to fetch payment history')
    }
}

/**
 * Fetches raw finance payments for an engagement assignment.
 *
 * @param assignmentId Engagement assignment id stored as the finance external id.
 * @returns Normalized payment rows without creator or billing-account name hydration.
 * @remarks Billing-account line-item reconciliation only needs payment amounts
 * and fees, so this avoids the extra profile and billing-account lookups used
 * by the full payment history modal.
 * @throws Error when the finance request fails.
 */
export async function fetchAssignmentPaymentSplits(
    assignmentId: number | string,
): Promise<AssignmentPayment[]> {
    const normalizedAssignmentId = String(assignmentId)
        .trim()

    if (!normalizedAssignmentId) {
        return []
    }

    try {
        const response = await xhrGetAsync<unknown>(
            `${TC_FINANCE_API_URL}/winnings/by-external-id/${encodeURIComponent(normalizedAssignmentId)}`,
        )

        return normalizePaymentsResponse(response)
    } catch (error) {
        throw normalizeError(error, 'Failed to fetch assignment payment splits')
    }
}

export async function createPayment(
    paymentData: Record<string, unknown>,
): Promise<AssignmentPayment> {
    try {
        return await xhrPostAsync<Record<string, unknown>, AssignmentPayment>(
            `${TC_FINANCE_API_URL}/winnings`,
            paymentData,
        )
    } catch (error) {
        throw normalizeError(error, 'Failed to create payment')
    }
}

export async function getPaymentsByAssignmentId(
    assignmentId: number | string,
): Promise<AssignmentPayment[]> {
    return fetchAssignmentPayments(assignmentId)
}

function normalizeTimesheetEntryIds(payment: AssignmentPayment): string[] {
    const rawEntryIds = payment.attributes?.timesheetEntryIds

    if (!Array.isArray(rawEntryIds)) {
        return []
    }

    const uniqueIds = new Set<string>()

    rawEntryIds.forEach(rawEntryId => {
        const entryId = String(rawEntryId || '')
            .trim()

        if (entryId) {
            uniqueIds.add(entryId)
        }
    })

    return [...uniqueIds]
}

/**
 * Detects when selected timesheet entries already appear on another payment for this assignment.
 *
 * This closes the crash gap between payment creation and timesheet-link persistence: if a previous
 * payment exists in finance but linking failed, the same entries are still protected from a second
 * payout attempt.
 */
export async function findTimesheetPaymentConflicts(
    assignmentId: number | string,
    entryIds: string[],
): Promise<TimesheetPaymentConflictSummary> {
    const normalizedEntryIds = Array.from(new Set(
        entryIds
            .map(entryId => String(entryId || '')
                .trim())
            .filter(Boolean),
    ))

    if (!normalizedEntryIds.length) {
        return {
            conflicts: [],
            overlappingEntryIds: [],
        }
    }

    const selectedEntryIds = new Set(normalizedEntryIds)
    const payments = await fetchAssignmentPaymentSplits(assignmentId)
    const conflicts: TimesheetPaymentConflict[] = []
    const overlappingEntryIds = new Set<string>()

    payments.forEach(payment => {
        const paymentEntryIds = normalizeTimesheetEntryIds(payment)

        if (!paymentEntryIds.length) {
            return
        }

        const matchedEntryIds = paymentEntryIds.filter(entryId => selectedEntryIds.has(entryId))

        if (!matchedEntryIds.length) {
            return
        }

        matchedEntryIds.forEach(entryId => overlappingEntryIds.add(entryId))

        conflicts.push({
            entryIds: matchedEntryIds,
            paymentReference: getPaymentReference(payment) || 'unknown',
        })
    })

    return {
        conflicts,
        overlappingEntryIds: [...overlappingEntryIds],
    }
}

/**
 * Durable identifier to record against the timesheet entries a payment consumed.
 *
 * Prefers the winning id the finance API returns, falling back to the payment id. Confirm with the
 * finance team which of the two survives before relying on it for reconciliation.
 */
export function getPaymentReference(payment: AssignmentPayment | undefined): string | undefined {
    const candidate = payment as (AssignmentPayment & {
        data?: {
            id?: number | string
            paymentId?: number | string
            payment_id?: number | string
            winningId?: number | string
            winning_id?: number | string
        }
        payment_id?: number | string
        winningId?: number | string
        winning_id?: number | string
    }) | undefined
    const reference = candidate?.id
        ?? candidate?.paymentId
        ?? candidate?.winningId
        ?? candidate?.winning_id
        ?? candidate?.payment_id
        ?? candidate?.data?.id
        ?? candidate?.data?.paymentId
        ?? candidate?.data?.winningId
        ?? candidate?.data?.winning_id
        ?? candidate?.data?.payment_id

    return reference === undefined || reference === null || reference === ''
        ? undefined
        : String(reference)
}
